import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Minus, Plus, Trash2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { useCart, formatEUR } from "../../context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, count, subtotal, updateQty, removeItem, clear } = useCart();
  const [success, setSuccess] = useState(false);

  const onCheckout = () => {
    setSuccess(true);
    // Keep the cart intact — it's a dummy checkout. Auto-dismiss after a moment.
    setTimeout(() => setSuccess(false), 3200);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0 bg-white"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 sm:px-7 pt-6 pb-4 border-b border-qh-line text-left">
          <SheetTitle className="font-display text-2xl font-semibold text-qh-ink">
            Dein Warenkorb
          </SheetTitle>
          <SheetDescription className="text-qh-slate">
            {count === 0
              ? "Noch leer. Zeit, echte Begegnungen vorzubereiten."
              : `${count} Artikel · ${formatEUR(subtotal)} gesamt`}
          </SheetDescription>
        </SheetHeader>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-7 py-6">
          {items.length === 0 ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center text-qh-slate gap-4"
              data-testid="cart-empty"
            >
              <div className="h-14 w-14 rounded-full bg-[#FFE4E6] flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-qh-rose" strokeWidth={1.5} />
              </div>
              <p className="max-w-xs text-sm leading-relaxed">
                Wähle ein QHeart-Paket und starte echte Verbindungen — direkt im
                echten Leben.
              </p>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="rounded-full text-sm"
              >
                Weiter stöbern
              </Button>
            </div>
          ) : (
            <ul className="space-y-5" data-testid="cart-items">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="flex gap-4 items-start"
                  data-testid={`cart-item-${it.id}`}
                >
                  <div className="shrink-0 h-20 w-20 rounded-xl bg-[#FFFBFB] border border-[#FFE4E6] overflow-hidden">
                    <img
                      src="/assets/qheart-front.png"
                      alt={it.name}
                      className="w-full h-full object-contain p-2"
                      draggable="false"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-display text-base font-medium text-qh-ink">
                          {it.name}
                        </h4>
                        <p className="text-xs text-qh-slate mt-0.5">
                          {formatEUR(it.price)} je Paket
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        className="text-qh-slate hover:text-qh-rose transition-colors"
                        aria-label="Entfernen"
                        data-testid={`cart-remove-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-qh-line overflow-hidden">
                        <button
                          onClick={() => updateQty(it.id, it.qty - 1)}
                          className="h-8 w-8 flex items-center justify-center hover:bg-[#FFFBFB] transition-colors"
                          aria-label="Weniger"
                          data-testid={`cart-dec-${it.id}`}
                        >
                          <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <span className="min-w-[28px] text-center text-sm font-medium">
                          {it.qty}
                        </span>
                        <button
                          onClick={() => updateQty(it.id, it.qty + 1)}
                          className="h-8 w-8 flex items-center justify-center hover:bg-[#FFFBFB] transition-colors"
                          aria-label="Mehr"
                          data-testid={`cart-inc-${it.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                      <div className="font-display text-base font-semibold text-qh-ink">
                        {formatEUR(it.qty * it.price)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 sm:px-7 py-5 border-t border-qh-line bg-[#FFFBFB]">
            {success ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-qh-rose/30 bg-[#FFE4E6]/50 p-4 mb-3"
                data-testid="cart-checkout-success"
              >
                <CheckCircle2 className="w-5 h-5 text-qh-rose shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-sm text-qh-ink leading-relaxed">
                  <span className="font-semibold">Checkout wird bald verfügbar sein.</span>{" "}
                  Wir melden uns, sobald du bezahlen kannst.
                </div>
              </div>
            ) : null}

            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-qh-slate">Gesamt</span>
              <span
                className="font-display text-2xl font-semibold text-qh-ink"
                data-testid="cart-total"
              >
                {formatEUR(subtotal)}
              </span>
            </div>
            <Button
              onClick={onCheckout}
              className="w-full h-12 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white font-medium transition-all hover:-translate-y-0.5"
              data-testid="cart-checkout"
            >
              Jetzt bezahlen
            </Button>
            <div className="mt-3 flex items-center justify-between text-xs">
              <button
                onClick={clear}
                className="text-qh-slate hover:text-qh-ink underline underline-offset-2"
                data-testid="cart-clear"
              >
                Warenkorb leeren
              </button>
              <span className="text-qh-slate">Versand & Steuern im Checkout</span>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
